/* ============================================================
   ShoLi — Settings UI Module
   Loads settings into form, binds save, handles data export/import.
   Depends on State, ViewManager, Toast, Renderer.
   ============================================================ */

import type { ViewManagerApi } from "../lib/view-manager";
import type { AppSettings } from "../state";

// Forward declarations — provided by app.ts composition root
type StateApi = {
  loadSettings: () => AppSettings;
  saveSettings: (newSettings: Partial<AppSettings>) => void;
  getSetting: <K extends keyof AppSettings>(key: K) => AppSettings[K];
  exportData: () => string;
  importData: (
    text: string,
    mergePolicy?: "keep" | "import",
  ) => { imported: number; updated: number };
  eraseAll: () => void;
};

type ToastApi = {
  show: (message: string, duration?: number) => void;
};

type ConfirmDialogApi = {
  ask: (message: string) => Promise<boolean>;
};

function getEl(id: string): HTMLElement | null {
  return document.getElementById(id);
}

function getInput(id: string): HTMLInputElement {
  return getEl(id) as HTMLInputElement;
}

function getSelect(id: string): HTMLSelectElement {
  return getEl(id) as HTMLSelectElement;
}

export class SettingsUI {
  private state: StateApi;
  private viewManager: ViewManagerApi;
  private toast: ToastApi;
  private confirm: ConfirmDialogApi;
  private renderer: import("./renderer").Renderer | null = null;

  constructor(
    state: StateApi,
    viewManager: ViewManagerApi,
    toast: ToastApi,
    confirm: ConfirmDialogApi,
  ) {
    this.state = state;
    this.viewManager = viewManager;
    this.toast = toast;
    this.confirm = confirm;
  }

  setRenderer(renderer: import("./renderer").Renderer): void {
    this.renderer = renderer;
  }

  loadIntoUI(): void {
    const s = this.state.loadSettings();

    getInput("setting-confirm-remove-checked").checked = s.confirmRemoveChecked;
    getInput("setting-confirm-remove-all").checked = s.confirmRemoveAll;
    getInput("setting-marker-off").value = s.markerOffList;
    getInput("setting-marker-unchecked").value = s.markerUnchecked;
    getInput("setting-marker-checked").value = s.markerChecked;
    getSelect("setting-text-size").value = s.textSize;
    getInput("setting-group-by-state").checked = s.groupByState;
  }

  bindSave(): void {
    document.getElementById("btn-save-settings")?.addEventListener("click", () => {
      const newSettings: Partial<AppSettings> = {
        confirmRemoveChecked: getInput("setting-confirm-remove-checked").checked,
        confirmRemoveAll: getInput("setting-confirm-remove-all").checked,
        markerChecked: getInput("setting-marker-checked").value || "+",
        markerUnchecked: getInput("setting-marker-unchecked").value || "-",
        markerOffList: getInput("setting-marker-off").value || "*",
        textSize: getSelect("setting-text-size").value,
        groupByState: getInput("setting-group-by-state").checked,
      };
      this.state.saveSettings(newSettings);
      if (this.renderer) this.renderer.applyTextSize();
      this.toast.show("Settings saved");
      this.viewManager.goBack();
    });
  }

  bindDataView(): void {
    document.getElementById("btn-export")?.addEventListener("click", () => {
      const text = this.state.exportData();
      const ta = document.getElementById("export-textarea") as HTMLTextAreaElement | null;
      if (ta) ta.value = text;
      document.getElementById("data-export-area")?.classList.remove("hidden");
      document.getElementById("data-import-area")?.classList.add("hidden");
    });

    document.getElementById("btn-copy-export")?.addEventListener("click", () => {
      const textarea = document.getElementById("export-textarea") as HTMLTextAreaElement | null;
      if (!textarea) return;
      textarea.select();
      navigator.clipboard
        .writeText(textarea.value)
        .then(() => {
          this.toast.show("Copied to clipboard");
        })
        .catch(() => {
          document.execCommand("copy");
          this.toast.show("Copied to clipboard");
        });
    });

    document.getElementById("btn-import")?.addEventListener("click", () => {
      document.getElementById("data-import-area")?.classList.remove("hidden");
      document.getElementById("data-export-area")?.classList.add("hidden");
    });

    document.getElementById("btn-do-import")?.addEventListener("click", () => {
      const ta = document.getElementById("import-textarea") as HTMLTextAreaElement | null;
      if (!ta) return;
      const text = ta.value;
      const sel = document.getElementById("import-merge-policy") as HTMLSelectElement | null;
      if (!sel) return;
      const policy = sel.value as "keep" | "import";
      const result = this.state.importData(text, policy);
      this.toast.show(`Imported ${result.imported}, updated ${result.updated}`);
      document.getElementById("data-import-area")?.classList.add("hidden");
    });

    document.getElementById("file-import")?.addEventListener("change", (e) => {
      const file = (e.currentTarget as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const target = document.getElementById("import-textarea") as HTMLTextAreaElement | null;
        if (target && ev.target?.result) {
          target.value = ev.target.result as string;
        }
      };
      reader.readAsText(file);
    });

    document.getElementById("btn-erase-all")?.addEventListener("click", async () => {
      const ok = await this.confirm.ask("ERASE ALL DATA? This cannot be undone.");
      if (!ok) return;
      this.state.eraseAll();
      this.toast.show("All data erased");
    });
  }
}
